/**
 * Centralized logout utility
 *
 * Clears all user-specific localStorage data while preserving tenant information
 * for automatic redirect functionality on the root page.
 */

import { supabase } from './supabaseClient';
import { store, resetAllState } from '../store/store';
import { safeGetItem, safeLocalKeys, safeRemoveItem, safeSessionKeys, safeSessionRemoveItem } from "@mediatechgroup/app-version";

/**
 * Keys to ALWAYS clear (explicit list for clarity and future maintenance)
 */
const KEYS_TO_CLEAR = [
  // Auth
  'authToken',
  'userRole',
  'userDetails',
  'authSource',

  // Settings (tenant-specific, should reload on next login)
  'settings',
  'clubName',

  // Version/Update notifications
  'app_version_info',
  'app_update_dismissed_version',
  'appUpdateRequested',
  'appUpdateRequestedGitHash',
  'appUpdateDismissed',

  // Overdue actions modal
  'showOverdueActionsModal',
  'overdueActions',

  // Membership details (temporary storage)
  'membershipDetails',

  // Current tenant context
  'currentTenant',
  // Legacy duplicate of the tenant slug — no longer written, but cleared so a
  // stale value from a prior session/app version can't leak across users.
  'tenantID',

  // Kiosk mode
  'kiosk_auth_token',
  'kiosk_user',
  'kiosk_device_token',
];

/**
 * sessionStorage belongs to the browser TAB, not to the person signed in, so
 * it outlives a sign-out. Anything in it that describes one person's sign-in
 * has to be removed here, or the next person to sign in on the same tab
 * inherits it (the yearly-waiver pop-up had to key itself on the sign-in to
 * work around that).
 *
 * Cleared on every sign-out, including an expired session:
 *   pendingWaiversShownFor      "the waiver pop-up was already shown"
 *   staff_ai_session_id         the staff assistant conversation
 *   invoiceMonthFilter          the month picked on the Memberships list
 *   ownerDataHealthDismissed:*  "I dismissed today's attention banner"
 *
 * Deliberately LEFT, each for a reason (do not add these):
 *   postLoginDestination        the page to return to after signing in again;
 *                               it is written just before the sign-in page
 *                               and read once after it
 *   broadcast-wizard-state      an unsent broadcast being written; an expired
 *                               session must not lose it
 *   analytics_session_id        an anonymous visit id, not a person
 *   embedStyleOverrides         how an embedded page is styled
 *   cc_chunk_reload_at          stops a reload loop after a new release
 *   anon_ai_*                   the public chat, meant to carry on across a
 *                               sign-in
 */
export const SESSION_KEYS_TO_CLEAR = [
  'pendingWaiversShownFor',
  'staff_ai_session_id',
  'invoiceMonthFilter',
];
export const SESSION_KEY_PREFIXES_TO_CLEAR = ['ownerDataHealthDismissed:'];

/**
 * A payment already taken during a sign-up (registration/takenPayment.ts).
 * It must survive anything that can happen INSIDE the sign-up, including an
 * old sign-in on the same tab expiring halfway through, so it is not in the
 * lists above. A person pressing Sign out is never part of a sign-up, so it
 * is removed then, and only then.
 */
export const SESSION_KEY_PREFIXES_TO_CLEAR_ON_SIGN_OUT = ['registration-payment-taken:'];

const clearSessionKeys = (keys: string[], prefixes: string[]): void => {
  try {
    keys.forEach((key) => safeSessionRemoveItem(key));
    // The key list is read once up front, so removing an entry does not skip one.
    for (const key of safeSessionKeys()) {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        safeSessionRemoveItem(key);
      }
    }
  } catch {
    // sessionStorage blocked: there is nothing stored to clear.
  }
};

/**
 * Clear all user-specific data from localStorage, and the per-sign-in
 * entries in sessionStorage.
 * Preserves tenant info for redirect functionality
 */
export const clearUserData = (): void => {
  clearSessionKeys(SESSION_KEYS_TO_CLEAR, SESSION_KEY_PREFIXES_TO_CLEAR);

  // First, clear all explicitly known keys
  KEYS_TO_CLEAR.forEach(key => {
    safeRemoveItem(key);
  });

  // Then, clear any onboarding-related keys (dynamic pattern)
  // PLUS any Stripe / payment-intent / setup-intent identifiers that
  // various flows may have stashed on the user's device. Defense in
  // depth — if next person uses this browser, no stale payment
  // identifier should be reachable that points at the previous user's
  // Stripe customer. Pattern catches `stripe_customer_id`,
  // `payment_intent_*`, `setup_intent_*`, `stripeCustomer*`,
  // `paymentMethodId`, etc.
  const allKeys = safeLocalKeys();
  allKeys.forEach(key => {
    if (key.startsWith('onboarding-')) {
      safeRemoveItem(key);
    } else if (/stripe|payment[_-]?intent|setup[_-]?intent|payment[_-]?method/i.test(key)) {
      safeRemoveItem(key);
    }
  });
};

/**
 * Perform complete logout
 * - Signs out from Supabase
 * - Clears all user-specific localStorage data
 * - Resets the Redux store
 * - Preserves tenant info for redirect
 *
 * @returns The tenant slug (if available) for redirect purposes
 */
export const performLogout = async (): Promise<string | null> => {
  // Get tenant slug before clearing (for redirect)
  const tenantSlug = safeGetItem('tenantSlug');

  // Sign out from Supabase
  await supabase.auth.signOut();

  // Clear user data
  clearUserData();
  // A person chose to sign out: also drop any half-finished sign-up payment
  // note left on this tab (see SESSION_KEY_PREFIXES_TO_CLEAR_ON_SIGN_OUT).
  clearSessionKeys([], SESSION_KEY_PREFIXES_TO_CLEAR_ON_SIGN_OUT);

  // Clearing localStorage is NOT enough. Both logout handlers `navigate()` to
  // /login, an SPA route change with no page reload, so the in-memory Redux
  // store survives — carrying the previous user's `membership`, including
  // their stripe_customer_id. The next person to sign in on that tab is then
  // shown, and queried against, that membership: PaymentModal reads the stale
  // customer id and the ownership gate answers 403, so they cannot pay. That
  // is the "I tried to pay online and it wouldn't let me" report.
  // See resetAllState in store/store.tsx for the full account.
  store.dispatch(resetAllState());

  return tenantSlug;
};

/**
 * Get the preserved tenant slug for redirect
 */
export const getPreservedTenantSlug = (): string | null => {
  return safeGetItem('tenantSlug');
};

export default performLogout;
